import { Link } from "react-router-dom";
import { Logo, LogoWordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  ScanLine, FileSignature, FolderLock, Landmark, ShieldCheck,
  Zap, HeartHandshake, ArrowRight, Check,
} from "lucide-react";

const FEATURES = [
  { icon: ScanLine, title: "Scanner IA", text: "Photographiez un courrier, obtenez une fiche claire en 8 blocs : ce qu'on vous demande, les documents, la date limite et le risque." },
  { icon: FileSignature, title: "Courriers prêts à envoyer", text: "Recours, demande de délai, remise de dette… générés en français parfait et exportables en PDF." },
  { icon: FolderLock, title: "Coffre-fort", text: "Rangez vos justificatifs, réutilisez-les et recevez une alerte avant leur péremption." },
  { icon: Landmark, title: "Assistant Impôts", text: "Comprenez chaque case et gardez l'œil sur le calendrier fiscal officiel, sans jamais rien inventer." },
];

const BADGES = [
  { icon: ShieldCheck, label: "Sûr & fiable" },
  { icon: Zap, label: "Simple & rapide" },
  { icon: HeartHandshake, label: "Accompagné à chaque étape" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 bg-white border-b border-border">
        <div className="mx-auto max-w-6xl px-4 h-16 flex items-center justify-between">
          <LogoWordmark />
          <div className="flex items-center gap-2">
            <Link to="/connexion">
              <Button variant="ghost" className="rounded-full" data-testid="landing-login-btn">
                Se connecter
              </Button>
            </Link>
            <Link to="/inscription">
              <Button className="rounded-full bg-brand hover:bg-brand/90" data-testid="landing-register-btn">
                Commencer
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 pt-14 pb-10 grid md:grid-cols-2 gap-10 items-center">
        <div className="adminia-fade-in">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand/10 text-brand px-4 py-1.5 text-sm font-medium mb-5">
            <span className="text-base">🇫🇷</span> Essai gratuit de 7 jours
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand leading-[1.05]">
            Vos démarches<br />administratives,<br />enfin simplifiées.
          </h1>
          <p className="mt-5 text-lg text-foreground/80 max-w-md">
            Prenez en photo un courrier de la CAF, des impôts, de la préfecture ou de la CPAM.
            ADMINIA vous explique en français simple ce que vous devez faire, étape par étape.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/inscription">
              <Button size="lg" className="rounded-full h-13 px-7 text-base bg-brand hover:bg-brand/90 gap-2" data-testid="hero-cta">
                Analyser mon 1er courrier <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-4">
            {BADGES.map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-sm font-medium text-foreground/70">
                <b.icon className="h-5 w-5 text-brand" /> {b.label}
              </div>
            ))}
          </div>
        </div>
        <div className="flex justify-center">
          <div className="rounded-3xl border border-border bg-white shadow-sm p-6 max-w-sm w-full">
            <Logo size={220} className="mx-auto" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-surface border-y border-border py-16">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="font-heading font-bold text-3xl text-center mb-3">Tout au même endroit</h2>
          <p className="text-center text-muted-foreground max-w-xl mx-auto mb-10">
            Une application pensée pour vous rassurer et vous faire gagner du temps.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f) => (
              <Card key={f.title} className="p-6 bg-white border-border/70 hover:shadow-md transition-shadow duration-200">
                <div className="h-11 w-11 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                  <f.icon className="h-6 w-6 text-brand" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{f.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{f.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Anti-hallucination trust */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <ShieldCheck className="h-10 w-10 text-brand mx-auto mb-4" />
        <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-3">Des informations fiables, jamais inventées</h2>
        <p className="text-foreground/75 leading-relaxed">
          Les informations juridiques et fiscales proviennent d'une base interne sourcée et vérifiée.
          ADMINIA ne devine jamais une règle ou une date : chaque information renvoie vers le site officiel.
        </p>
      </section>

      {/* CTA */}
      <section className="bg-brand text-white py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="font-heading font-bold text-3xl mb-4">Prêt à reprendre le contrôle ?</h2>
          <div className="flex flex-wrap justify-center gap-4 mb-6 text-sm">
            {["3 analyses offertes / mois", "Sans engagement", "Annulable à tout moment"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><Check className="h-4 w-4" /> {t}</span>
            ))}
          </div>
          <Link to="/inscription">
            <Button size="lg" className="rounded-full h-13 px-8 text-base bg-white text-brand hover:bg-white/90" data-testid="footer-cta">
              Créer mon compte gratuit
            </Button>
          </Link>
        </div>
      </section>

      <footer className="py-8 text-center text-sm text-muted-foreground">
        <p>ADMINIA — vos démarches simplifiées</p>
      </footer>
    </div>
  );
}
